import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DollarSign, TrendingUp, Briefcase, Plus, Calendar, 
  Instagram, Twitter, Youtube, CheckCircle, Clock,
  Target, Award, Users, ChevronRight, Star, Edit,
  Trash2, ExternalLink, Building2, Handshake
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const NIL_CATEGORIES = [
  { id: 'social_media', name: 'Social Media', icon: Instagram },
  { id: 'endorsement', name: 'Brand Endorsement', icon: Award },
  { id: 'appearance', name: 'Appearances', icon: Users },
  { id: 'camp', name: 'Camps & Clinics', icon: Target },
  { id: 'merchandise', name: 'Merchandise', icon: Briefcase },
  { id: 'autograph', name: 'Autographs', icon: Edit },
];

const NilDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    activeDeals: 0,
    pendingDeals: 0,
    completedDeals: 0
  });
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [newDeal, setNewDeal] = useState({
    brandName: '',
    category: '',
    value: '',
    startDate: '',
    endDate: '',
    status: 'active',
    description: ''
  });

  useEffect(() => {
    fetchNilData();
  }, []);

  const fetchNilData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/nil/deals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedDeals = response.data.deals || [];
      setDeals(fetchedDeals);
      
      // Calculate stats from fetched deals
      const calculatedStats = {
        totalEarnings: fetchedDeals.reduce((sum, d) => sum + (d.value || 0), 0),
        activeDeals: fetchedDeals.filter(d => d.status === 'active').length,
        pendingDeals: fetchedDeals.filter(d => d.status === 'pending').length,
        completedDeals: fetchedDeals.filter(d => d.status === 'completed').length
      };
      setStats(calculatedStats);
    } catch (error) {
      console.error('Failed to fetch NIL deals:', error);
      setDeals([]);
      setStats({
        totalEarnings: 0,
        activeDeals: 0,
        pendingDeals: 0,
        completedDeals: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeal = async () => {
    if (!newDeal.brandName || !newDeal.category || !newDeal.value) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('aura_token');
      const dealData = {
        brandName: newDeal.brandName,
        category: newDeal.category,
        value: parseFloat(newDeal.value),
        startDate: newDeal.startDate || null,
        endDate: newDeal.endDate || null,
        status: newDeal.status,
        description: newDeal.description
      };
      
      await axios.post(`${API}/nil/deals`, dealData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deal added successfully!');
      setShowAddDeal(false);
      setNewDeal({
        brandName: '',
        category: '',
        value: '',
        startDate: '',
        endDate: '',
        status: 'active',
        description: ''
      });
      fetchNilData();
    } catch (error) {
      console.error('Failed to add deal:', error);
      toast.error('Failed to add deal');
    }
  };

  const handleDeleteDeal = async (dealId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/nil/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deal deleted');
      fetchNilData();
    } catch (error) {
      console.error('Failed to delete deal:', error);
      toast.error('Failed to delete deal');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      case 'expired': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getCategoryIcon = (categoryId) => {
    const category = NIL_CATEGORIES.find(c => c.id === categoryId);
    return category?.icon || Briefcase;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              NIL Dashboard
            </h1>
            <p className="text-aura-text-muted mt-1">Track your Name, Image, Likeness deals and earnings</p>
          </div>
          <Dialog open={showAddDeal} onOpenChange={setShowAddDeal}>
            <DialogTrigger asChild>
              <Button className="bg-green-500 hover:bg-green-600 text-white" data-testid="add-deal-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Deal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl uppercase">Add NIL Deal</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Brand/Company Name *</Label>
                  <Input
                    value={newDeal.brandName}
                    onChange={(e) => setNewDeal({...newDeal, brandName: e.target.value})}
                    placeholder="e.g., Nike, Local Business"
                    className="bg-aura-bg border-white/10 text-white mt-1"
                    data-testid="deal-brand-input"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={newDeal.category} onValueChange={(v) => setNewDeal({...newDeal, category: v})}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {NIL_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Deal Value ($) *</Label>
                  <Input
                    type="number"
                    value={newDeal.value}
                    onChange={(e) => setNewDeal({...newDeal, value: e.target.value})}
                    placeholder="5000"
                    className="bg-aura-bg border-white/10 text-white mt-1"
                    data-testid="deal-value-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={newDeal.startDate}
                      onChange={(e) => setNewDeal({...newDeal, startDate: e.target.value})}
                      className="bg-aura-bg border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={newDeal.endDate}
                      onChange={(e) => setNewDeal({...newDeal, endDate: e.target.value})}
                      className="bg-aura-bg border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={newDeal.status} onValueChange={(v) => setNewDeal({...newDeal, status: v})}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      <SelectItem value="active" className="text-white">Active</SelectItem>
                      <SelectItem value="pending" className="text-white">Pending</SelectItem>
                      <SelectItem value="completed" className="text-white">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={newDeal.description}
                    onChange={(e) => setNewDeal({...newDeal, description: e.target.value})}
                    placeholder="Brief description of the deal"
                    className="bg-aura-bg border-white/10 text-white mt-1"
                  />
                </div>
                <Button 
                  onClick={handleAddDeal} 
                  className="w-full bg-green-500 hover:bg-green-600"
                  data-testid="save-deal-btn"
                >
                  Save Deal
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-green-400 uppercase tracking-wider">Total Earnings</p>
                  <p className="font-heading font-bold text-2xl text-white">
                    ${stats.totalEarnings.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">Active Deals</p>
                  <p className="font-heading font-bold text-2xl text-white">{stats.activeDeals}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">Pending</p>
                  <p className="font-heading font-bold text-2xl text-white">{stats.pendingDeals}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Handshake className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">Completed</p>
                  <p className="font-heading font-bold text-2xl text-white">{stats.completedDeals}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Readiness Link */}
        <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/10 border-aura-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Target className="w-8 h-8 text-aura-primary" />
                <div>
                  <p className="font-heading font-bold text-white uppercase">Improve Your NIL Potential</p>
                  <p className="text-sm text-aura-text-muted">Higher Readiness Score = More NIL Opportunities</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/readiness')}
                variant="outline"
                className="border-aura-primary/50 text-aura-primary hover:bg-aura-primary/10"
              >
                View Readiness <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Deals List */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Your NIL Deals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {deals.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <p className="text-aura-text-muted">No NIL deals yet</p>
                <p className="text-sm text-aura-text-subtle mt-1">
                  Add your first deal to start tracking your earnings
                </p>
                <Button 
                  onClick={() => setShowAddDeal(true)}
                  className="mt-4 bg-green-500 hover:bg-green-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Deal
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {deals.map(deal => {
                  const Icon = getCategoryIcon(deal.category);
                  const brandName = deal.brandName;
                  const startDate = deal.startDate;
                  const endDate = deal.endDate;
                  const dealId = deal.id;
                  return (
                    <div 
                      key={dealId}
                      className="flex items-center gap-4 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                      data-testid={`deal-${dealId}`}
                    >
                      <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{brandName}</p>
                          <Badge className={`${getStatusColor(deal.status)} border-0 capitalize`}>
                            {deal.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-aura-text-muted">
                          {NIL_CATEGORIES.find(c => c.id === deal.category)?.name || deal.category}
                          {deal.description && ` • ${deal.description}`}
                        </p>
                        {startDate && (
                          <p className="text-xs text-aura-text-subtle mt-1">
                            {new Date(startDate).toLocaleDateString()} 
                            {endDate && ` - ${new Date(endDate).toLocaleDateString()}`}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-heading font-bold text-xl text-green-400">
                          ${(deal.value || 0).toLocaleString()}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDeal(dealId)}
                          className="text-aura-text-muted hover:text-red-400"
                          data-testid={`delete-deal-${dealId}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips Section */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              NIL Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="font-medium text-white mb-2">Build Your Social Presence</p>
                <p className="text-sm text-aura-text-muted">
                  Brands look at follower count and engagement. Post consistently about your training journey.
                </p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="font-medium text-white mb-2">Stay Compliant</p>
                <p className="text-sm text-aura-text-muted">
                  Always check with your school's compliance office before signing any deal.
                </p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="font-medium text-white mb-2">Know Your Worth</p>
                <p className="text-sm text-aura-text-muted">
                  Use your Readiness Score and performance metrics to negotiate fair deals.
                </p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="font-medium text-white mb-2">Track Everything</p>
                <p className="text-sm text-aura-text-muted">
                  Keep records of all deals for tax purposes. Consider working with an accountant.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default NilDashboardPage;
